// Section 0. Reuse a previous design?
//
// Three tiles: Start fresh / Reuse from cart / From a past order.
// "Reuse from cart" only renders when the cart already has rk-builder
// lines (decodeable on mount). "From a past order" is always available
// and expands an inline form (order # + email) that hits the worker's
// /api/order-spots route. Successful results render as the same
// DesignCard grid as the cart path.

import * as React from "react";
import { useOpenScroll } from "../util/scroll";
import { flowCopy } from "../copy";
import {
  fetchPastOrderSpots,
  ingestProof,
  type PastOrderProof,
  type PastOrderSpot,
} from "../api";
import type { Config } from "../types";
import type { ReusableDesign } from "../reuse";
import { DesignCard } from "../components/DesignCard";
import type { EmbroideryDraft } from "./PlacementSection";

type Mode = "fresh" | "cart" | "order" | null;

export function ReuseSection({
  config,
  workerUrl,
  designs,
  defaultEmail,
  deepLink,
  onPick,
  onPickProof,
  open,
  onToggleSection,
  onStartFresh,
}: {
  config: Config;
  /** Worker base URL, used for the /api/order-spots POST. */
  workerUrl: string;
  /** Designs already decoded from the customer's current cart. May be
   *  empty — the "Reuse from cart" tile only shows when this has rows. */
  designs: ReusableDesign[];
  /** Pre-fill the email field when the storefront knows the customer's
   *  email (logged-in shoppers). Cuts the lookup down to one field. */
  defaultEmail: string;
  /** 2026-07-02 CRO 2.5: parsed ?rk_reuse deep link (Klaviyo reorder
   *  emails). When set, the section jumps straight into "From a past
   *  order" with the form pre-filled; if the email came through too the
   *  lookup fires immediately, so the customer lands on their designs. */
  deepLink?: { orderName: string; email: string | null } | null;
  onPick: (draft: EmbroideryDraft) => void;
  /** Phase 31: customer ticked a verified past-order proof PDF. Opens a
   *  fresh logo-mode draft with the proof set as the upload — already-
   *  digitised, no $30 fee. */
  onPickProof: (ingested: {
    stagingKey: string;
    contentType: string;
    fileHash: string;
    filename: string;
    sourceOrderName: string;
  }) => void;
  open: boolean;
  onToggleSection: () => void;
  /** Customer picked "Start fresh" — open a blank editor + advance the
   *  top-level flow to the Customise section. */
  onStartFresh: () => void;
}) {
  const [mode, setMode] = React.useState<Mode>(null);
  const sectionRef = useOpenScroll<HTMLElement>(open);

  // Past-order lookup state. Only used when mode === "order".
  const [orderName, setOrderName] = React.useState("");
  const [email, setEmail] = React.useState(defaultEmail);
  // Keep the email in sync when the parent learns about it after mount
  // (e.g. customer hits a logged-in storefront after first render).
  React.useEffect(() => {
    if (defaultEmail && !email) setEmail(defaultEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmail]);

  // 2026-07-02 CRO 2.5: reorder deep link. Jump straight into the past-
  // order flow with the form seeded from the URL params App.tsx parsed.
  // When the email came through (or the storefront knows the logged-in
  // customer), the lookup fires immediately so the customer's first
  // screen is their own designs; otherwise they only have to type their
  // email. Once-only via ref, so re-renders never re-fire the lookup.
  const deepLinkFiredRef = React.useRef(false);
  React.useEffect(() => {
    if (!deepLink || deepLinkFiredRef.current) return;
    deepLinkFiredRef.current = true;
    setMode("order");
    setOrderName(deepLink.orderName);
    const lookupEmail = deepLink.email ?? (defaultEmail || "");
    if (lookupEmail) {
      setEmail(lookupEmail);
      void runLookup(deepLink.orderName, lookupEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);
  const [loading, setLoading] = React.useState(false);
  const [orderError, setOrderError] = React.useState<string | null>(null);
  const [orderSpots, setOrderSpots] = React.useState<PastOrderSpot[]>([]);
  // Phase 30: order-level design proofs (PDFs from timeline comments).
  // Separate from spots because they're per-order not per-spot.
  const [orderProofs, setOrderProofs] = React.useState<PastOrderProof[]>([]);
  // URL of the proof currently being ingested. Used to disable the
  // tile while the worker downloads/stages so a double-click doesn't
  // fire two ingests in parallel.
  const [ingestingUrl, setIngestingUrl] = React.useState<string | null>(null);
  const [searched, setSearched] = React.useState(false);

  const cartHasReusable = designs.length > 0;

  // Phase 31: tile click triggers a verified ingest. Worker re-checks
  // the URL belongs to the order on every call, then downloads + stages
  // the PDF to R2. The returned stagingKey carries alreadyDigitised:true
  // so the new draft skips the $30 digitisation fee.
  const pickProof = async (proof: PastOrderProof) => {
    setIngestingUrl(proof.url);
    setOrderError(null);
    const r = await ingestProof(
      workerUrl,
      orderName.trim(),
      email.trim(),
      proof.url,
    );
    setIngestingUrl(null);
    if (!r.ok || !r.stagingKey || !r.contentType || !r.fileHash || !r.filename || !r.sourceOrderName) {
      setOrderError(
        "Couldn't grab that design just now. Try again, or upload your design manually below.",
      );
      return;
    }
    onPickProof({
      stagingKey: r.stagingKey,
      contentType: r.contentType,
      fileHash: r.fileHash,
      filename: r.filename,
      sourceOrderName: r.sourceOrderName,
    });
  };

  // Explicit args let the deep-link effect fire the lookup in the same
  // tick it seeds the form state (React state wouldn't have flushed yet).
  // The button path passes nothing and reads the live form state.
  const runLookup = async (
    lookupOrder: string = orderName,
    lookupEmail: string = email,
  ) => {
    setLoading(true);
    setOrderError(null);
    setSearched(true);
    const r = await fetchPastOrderSpots(workerUrl, lookupOrder.trim(), lookupEmail.trim());
    setLoading(false);
    if (!r.ok) {
      setOrderSpots([]);
      setOrderProofs([]);
      // Translate the worker's machine codes into customer copy. Anything
      // else surfaces as a generic "we couldn't find that order" so a
      // misconfigured shop secret doesn't leak.
      if (r.error === "not_found") {
        setOrderError(
          "We couldn't find that order. Double-check the order number + email match what's on your receipt.",
        );
      } else if (r.error === "rate_limited") {
        setOrderError("Too many tries. Wait a minute and try again.");
      } else {
        setOrderError("Lookup failed. Try again in a moment.");
      }
      return;
    }
    setOrderSpots(r.spots);
    setOrderProofs(r.proofs);
    if (r.spots.length === 0 && r.proofs.length === 0) {
      setOrderError(
        "That order doesn't have any reusable embroidery spots on it.",
      );
    }
  };

  const summary = (() => {
    if (mode === "cart") {
      return `Pick one of ${designs.length} design${designs.length === 1 ? "" : "s"} you've made before`;
    }
    if (mode === "order") {
      if (searched && orderSpots.length > 0) {
        return `${orderSpots.length} spot${orderSpots.length === 1 ? "" : "s"} from ${orderName.trim()}`;
      }
      return "Enter your order # + email to reuse spots from a past order.";
    }
    if (mode === "fresh") return "Designing from scratch. Start below.";
    if (cartHasReusable) {
      return `${designs.length} design${designs.length === 1 ? "" : "s"} you've made before, or reuse one from a past order`;
    }
    return "Start fresh or reuse a spot from one of your past orders.";
  })();

  return (
    <section ref={sectionRef} className={`rk-pc-sec${mode ? " is-active" : ""}`}>
      <button
        type="button"
        className="rk-pc-sec__head"
        onClick={onToggleSection}
        aria-expanded={open}
      >
        <span className="rk-pc-sec__num">
          {mode === "cart" || mode === "order" ? "↺" : "★"}
        </span>
        <span className="rk-pc-sec__heading">
          <span className="rk-pc-sec__title">
            {flowCopy(config, "reuseTitle", "Reuse a previous design?")}
          </span>
          <span className="rk-pc-sec__summary">{summary}</span>
        </span>
        <span className="rk-pc-sec__chevron" aria-hidden>
          {open ? "▾" : "▸"}
        </span>
      </button>
      <div className="rk-pc-collapse" data-open={open}>
      <div className="rk-pc-collapse__clip">
      <div className="rk-pc-sec__body">
        <div className="rk-pc-mode-row">
          <button
            type="button"
            className={`rk-pc-mode-tile${mode === "fresh" ? " is-selected" : ""}`}
            onClick={() => {
              setMode("fresh");
              onStartFresh();
            }}
          >
            <span className="rk-pc-mode-tile__title">Start fresh</span>
            <span className="rk-pc-mode-tile__desc">Design from scratch below.</span>
          </button>
          {cartHasReusable && (
            <button
              type="button"
              className={`rk-pc-mode-tile${mode === "cart" ? " is-selected" : ""}`}
              onClick={() => setMode("cart")}
            >
              <span className="rk-pc-mode-tile__title">Reuse a design in your cart</span>
              <span className="rk-pc-mode-tile__desc">
                {`Pick one of ${designs.length} design${designs.length === 1 ? "" : "s"} you've made before.`}
              </span>
            </button>
          )}
          <button
            type="button"
            className={`rk-pc-mode-tile${mode === "order" ? " is-selected" : ""}`}
            onClick={() => setMode("order")}
          >
            <span className="rk-pc-mode-tile__title">From a past order</span>
            <span className="rk-pc-mode-tile__desc">
              Enter your order # + email and we'll find your file.
            </span>
          </button>
        </div>

        {mode === "cart" && (
          <div className="rk-pc-design-grid">
            {designs.map((d) => (
              <DesignCard
                key={d.key}
                design={d}
                config={config}
                onPick={() => onPick(d.draft)}
              />
            ))}
          </div>
        )}

        {mode === "order" && (
          <div className="rk-pc-past-order">
            <div className="rk-pc-past-order__form">
              <label className="rk-pc-past-order__field">
                <span className="rk-pc-past-order__label">Order number</span>
                <input
                  type="text"
                  className="rk-pc-past-order__input"
                  placeholder="RK1234"
                  value={orderName}
                  onChange={(e) => setOrderName(e.target.value)}
                  inputMode="text"
                  autoComplete="off"
                />
              </label>
              <label className="rk-pc-past-order__field">
                <span className="rk-pc-past-order__label">Email</span>
                <input
                  type="email"
                  className="rk-pc-past-order__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
              <button
                type="button"
                className="rk-pc-past-order__btn"
                onClick={() => void runLookup()}
                disabled={loading || !orderName.trim() || !email.trim()}
              >
                {loading ? "Looking up…" : "Find my design"}
              </button>
            </div>
            {orderError && (
              <div className="rk-pc-past-order__error">{orderError}</div>
            )}
            {orderProofs.length > 0 && (
              // Phase 31: each proof is a clickable tile. Picking it
              // re-verifies the URL against admin, downloads the PDF
              // from Shopify's signed Google URL + stages to R2. The
              // ingested file becomes the active draft's logo with the
              // $30 setup fee waived (alreadyDigitised:true).
              <div className="rk-pc-past-proofs">
                <span className="rk-pc-past-proofs__label">
                  Tick a design proof to reuse it (no setup fee)
                </span>
                <div className="rk-pc-past-proofs__grid">
                  {orderProofs.map((p) => {
                    const isBusy = ingestingUrl === p.url;
                    return (
                      <button
                        key={p.url}
                        type="button"
                        className="rk-pc-past-proofs__tile"
                        onClick={() => pickProof(p)}
                        disabled={ingestingUrl !== null}
                      >
                        <span className="rk-pc-past-proofs__icon">PDF</span>
                        <span className="rk-pc-past-proofs__name">{p.name}</span>
                        <span className="rk-pc-past-proofs__cta">
                          {isBusy ? "Getting it…" : "Use as design"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {orderSpots.length > 0 && (
              <div className="rk-pc-design-grid">
                {orderSpots.map((d) => (
                  <DesignCard
                    key={d.key}
                    design={d}
                    config={config}
                    onPick={() => onPick(d.draft)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>
      </div>
    </section>
  );
}
